import { RefreshCw, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api/client';

const EMPTY_FORM = {
  openai: {
    api_key: '',
    model: 'gpt-5.5',
    timeout_seconds: '90',
    editorial_prompt: '',
  },
  wordpress: {
    base_url: '',
    username: '',
    app_password: '',
    default_status: 'draft',
    category_ids: '',
    tag_ids: '',
    timeout_seconds: '45',
  },
};

function fieldValue(settings, section, key, fallback = '') {
  return settings?.[section]?.[key]?.value || fallback;
}

function secretHint(settings, section, key) {
  const field = settings?.[section]?.[key];
  if (!field?.has_value) return 'nicht gesetzt';
  return `${field.value} (${field.source})`;
}

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState('');

  function hydrate(payload) {
    const next = payload.settings;
    setSettings(next);
    setForm({
      openai: {
        api_key: '',
        model: fieldValue(next, 'openai', 'model', 'gpt-5.5'),
        timeout_seconds: fieldValue(next, 'openai', 'timeout_seconds', '90'),
        editorial_prompt: fieldValue(next, 'openai', 'editorial_prompt'),
      },
      wordpress: {
        base_url: fieldValue(next, 'wordpress', 'base_url'),
        username: fieldValue(next, 'wordpress', 'username'),
        app_password: '',
        default_status: fieldValue(next, 'wordpress', 'default_status', 'draft'),
        category_ids: fieldValue(next, 'wordpress', 'category_ids'),
        tag_ids: fieldValue(next, 'wordpress', 'tag_ids'),
        timeout_seconds: fieldValue(next, 'wordpress', 'timeout_seconds', '45'),
      },
    });
  }

  async function load() {
    setLoading(true);
    setError('');
    setSaved(false);
    try {
      hydrate(await api.integrationSettings());
      await loadModels();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadModels() {
    setModelsLoading(true);
    setModelsError('');
    try {
      const payload = await api.openaiModels();
      setModels(payload.models || []);
      if (payload.error) setModelsError(payload.error);
    } catch (err) {
      setModelsError(err.message);
    } finally {
      setModelsLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function update(section, key, value) {
    setForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: value,
      },
    }));
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const payload = await api.updateIntegrationSettings(form);
      hydrate(payload);
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Admin</span>
          <h1>Integrationen</h1>
        </div>
        <button className="secondary-button" onClick={load} type="button">
          <RefreshCw size={16} />
          Refresh
        </button>
      </header>

      {error ? <div className="alert">{error}</div> : null}
      {saved ? <div className="success-alert">Gespeichert</div> : null}
      {loading ? <div className="screen-status">Einstellungen laden...</div> : null}

      <form className="settings-grid" onSubmit={submit}>
        <section className="settings-panel">
          <div className="section-heading">
            <h2>OpenAI</h2>
            <p>Wird fuer den Collection-Entwurf genutzt.</p>
          </div>
          <label>
            API Key
            <input
              value={form.openai.api_key}
              onChange={(event) => update('openai', 'api_key', event.target.value)}
              placeholder={secretHint(settings, 'openai', 'api_key')}
              type="password"
            />
          </label>
          <div className="two-col">
            <label>
              Modell
              <select value={form.openai.model} onChange={(event) => update('openai', 'model', event.target.value)}>
                {!models.some((model) => model.id === form.openai.model) ? <option value={form.openai.model}>{form.openai.model}</option> : null}
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.id}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Timeout Sekunden
              <input
                min="5"
                max="300"
                type="number"
                value={form.openai.timeout_seconds}
                onChange={(event) => update('openai', 'timeout_seconds', event.target.value)}
              />
            </label>
          </div>
          <div className="settings-inline-row">
            <button className="secondary-button" disabled={modelsLoading} onClick={loadModels} type="button">
              <RefreshCw size={16} />
              {modelsLoading ? 'Modelle laden...' : 'Modelle aktualisieren'}
            </button>
            {modelsError ? <span className="inline-error">{modelsError}</span> : null}
          </div>
          <label>
            Redaktionsprompt
            <textarea
              rows={5}
              value={form.openai.editorial_prompt}
              onChange={(event) => update('openai', 'editorial_prompt', event.target.value)}
            />
          </label>
        </section>

        <section className="settings-panel">
          <div className="section-heading">
            <h2>WordPress</h2>
            <p>Wird fuer Drafts und Featured Images genutzt.</p>
          </div>
          <label>
            Base URL
            <input
              value={form.wordpress.base_url}
              onChange={(event) => update('wordpress', 'base_url', event.target.value)}
              placeholder="https://example.de"
            />
          </label>
          <div className="two-col">
            <label>
              Benutzer
              <input value={form.wordpress.username} onChange={(event) => update('wordpress', 'username', event.target.value)} />
            </label>
            <label>
              App Password
              <input
                value={form.wordpress.app_password}
                onChange={(event) => update('wordpress', 'app_password', event.target.value)}
                placeholder={secretHint(settings, 'wordpress', 'app_password')}
                type="password"
              />
            </label>
          </div>
          <div className="two-col">
            <label>
              Status
              <select value={form.wordpress.default_status} onChange={(event) => update('wordpress', 'default_status', event.target.value)}>
                <option value="draft">Draft</option>
                <option value="pending">Pending Review</option>
                <option value="private">Private</option>
              </select>
            </label>
            <label>
              Timeout Sekunden
              <input
                min="5"
                max="300"
                type="number"
                value={form.wordpress.timeout_seconds}
                onChange={(event) => update('wordpress', 'timeout_seconds', event.target.value)}
              />
            </label>
          </div>
          <div className="two-col">
            <label>
              Kategorie-IDs
              <input value={form.wordpress.category_ids} onChange={(event) => update('wordpress', 'category_ids', event.target.value)} />
            </label>
            <label>
              Tag-IDs
              <input value={form.wordpress.tag_ids} onChange={(event) => update('wordpress', 'tag_ids', event.target.value)} />
            </label>
          </div>
        </section>

        <footer className="settings-actions">
          <button className="primary-button" disabled={saving} type="submit">
            <Save size={16} />
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </footer>
      </form>
    </div>
  );
}
